import { SupportingActorPropagationScope } from '@dadohr/shared/domainTypes/experience';
import styled from '@emotion/styled';
import {
  Button,
  ButtonBase,
  ButtonGroup,
  Popover,
  Typography
} from '@material-ui/core';
import { ChevronUp } from 'lucide-react';
import { useRef, useState } from 'react';
import { useUnsafeTranslation } from '../../../../../../i18next';

const ScopeMenu = styled(Popover)((p) => ({
  '& .MuiPopover-paper': {
    width: 340,
    padding: p.theme.spacing(0.75),
    marginTop: -p.theme.spacing(1)
  }
}));

const ScopeOption = styled(ButtonBase)<{ selected: boolean }>((p) => ({
  '&&': {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-start',
    width: '100%',
    padding: `${p.theme.spacing(1)}px ${p.theme.spacing(1.5)}px`,
    borderRadius: p.theme.custom.border.radius(),
    textAlign: 'left',
    backgroundColor: p.selected ? p.theme.custom.colors.blue.w100 : undefined
  },
  '&&:hover': {
    backgroundColor: p.selected
      ? p.theme.custom.colors.blue.w100
      : p.theme.custom.colors.gray.w100
  }
}));

// The save button for an actor edit that can reach live instances: the main
// button saves with the chosen scope, the caret picks the scope.
export const ScopeSaveButton = ({
  scope,
  onChangeScope,
  onSave,
  disabled
}: {
  scope: SupportingActorPropagationScope;
  onChangeScope: (nextScope: SupportingActorPropagationScope) => void;
  onSave: () => void;
  disabled?: boolean;
}) => {
  const { t } = useUnsafeTranslation();
  const groupRef = useRef<HTMLDivElement>(null);
  const [menuOpen, setMenuOpen] = useState(false);
  const options = [
    {
      scope: SupportingActorPropagationScope.ACTIVE_AND_NEW_INSTANCES,
      label: t('Save for all active experiences'),
      description: t('Applies to every currently running experience')
    },
    {
      scope: SupportingActorPropagationScope.NEW_INSTANCES_ONLY,
      label: t('Save for new experiences only'),
      description: t('Applies to experiences started from now on')
    }
  ];
  const selected = options.find((o) => o.scope === scope) || options[0];
  return (
    <>
      <ButtonGroup
        variant="contained"
        color="primary"
        disabled={disabled}
        ref={groupRef}
      >
        <Button onClick={onSave}>{selected.label}</Button>
        <Button
          size="small"
          aria-label={t('Choose which experiences to save for')}
          aria-haspopup="menu"
          aria-expanded={menuOpen ? 'true' : undefined}
          onClick={() => setMenuOpen(true)}
        >
          <ChevronUp size={16} />
        </Button>
      </ButtonGroup>
      <ScopeMenu
        open={menuOpen}
        anchorEl={groupRef.current}
        onClose={() => setMenuOpen(false)}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
        transformOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <div role="menu">
          {options.map((o) => (
            <ScopeOption
              key={o.scope}
              role="menuitemradio"
              aria-checked={o.scope === scope}
              selected={o.scope === scope}
              onClick={() => {
                onChangeScope(o.scope);
                setMenuOpen(false);
              }}
            >
              <Typography variant="h6">{o.label}</Typography>
              <Typography variant="caption">{o.description}</Typography>
            </ScopeOption>
          ))}
        </div>
      </ScopeMenu>
    </>
  );
};
